'use client'

import { useState } from 'react'
import { Plus, Minus } from 'lucide-react'

/**
 * Inline FAQ — Resmio buries answers behind a separate /faq page.
 * We surface the 6 questions that block a German Mittelstand owner
 * from signing up: price, lock-in, data location, deposit handling,
 * existing-website integration, and trial-end behavior.
 */
type FaqLang = 'de' | 'en' | 'tr'

const FAQ_T: Record<FaqLang, ReadonlyArray<{ q: string; a: string }>> = {
  de: [
    {
      q: 'Was kostet Rezaro pro Monat?',
      a: 'Drei transparente Preisstufen: Starter €29, Pro €69, Premium €119 pro Monat. Keine Provision pro Reservierung, keine versteckten Kosten, monatlich kündbar. Mehr als 5 Standorte oder Sonderwünsche? Dann sprechen wir individuell — alle anderen sind Self-Serve.',
    },
    {
      q: 'Wie lange bin ich gebunden?',
      a: 'Monatlich kündbar. Sie sehen das gebuchte Paket im Inhaber-Panel und beenden es mit einem Klick zum Ende des laufenden Monats. Es gibt keine 12-Monats-Verträge, keine Kündigungsfristen versteckt im Kleingedruckten.',
    },
    {
      q: 'Wo werden meine Gästedaten gespeichert?',
      a: 'Alle Daten — Reservierungen, Gästenamen, Telefonnummern, Notizen — liegen verschlüsselt auf Servern in Frankfurt am Main. Die Auftragsverarbeitung nach Art. 28 DSGVO finden Sie unter /avv. Es findet keine Datenübertragung in die USA oder ein Drittland statt.',
    },
    {
      q: 'Wie funktioniert die Anzahlung gegen No-Show?',
      a: 'Sie legen eine Schwelle fest — zum Beispiel: ab sechs Personen oder ab Freitag 19 Uhr wird eine Anzahlung von 10 € pro Person eingezogen. Das Geld wird auf der Karte des Gastes reserviert (Pre-Auth) und nur eingezogen, wenn der Gast nicht erscheint. Erscheint er, wird der Betrag freigegeben oder vom Rechnungsbetrag abgezogen.',
    },
    {
      q: 'Funktioniert Rezaro auch mit meiner bestehenden Website?',
      a: 'Ja. Sie erhalten ein kleines Code-Schnipsel (Widget), das wir in Ihre Website einbauen — egal ob WordPress, Wix, Jimdo oder selbst gebaut. Die Reservierung öffnet sich direkt auf Ihrer Seite, der Gast verlässt Ihre Domain nicht. Auch in Google-Profil und Instagram-Link integrierbar.',
    },
    {
      q: 'Was passiert nach den sieben Test-Tagen?',
      a: 'Sie haben bei der Anmeldung einen Plan und Ihre Karte hinterlegt. Am achten Tag wird die Pauschale automatisch eingezogen. Wenn Sie vorher kündigen — eine E-Mail an info@rezaro.de oder ein Klick im Panel reicht — passiert keine Abbuchung. Wir erinnern Sie 48 Stunden vorher per E-Mail.',
    },
  ],
  en: [
    {
      q: 'How much does Rezaro cost per month?',
      a: 'Three transparent pricing tiers: Starter €29, Pro €69, Premium €119 per month. No commission per reservation, no hidden costs, cancellable monthly. More than 5 locations or special requirements? Then we talk individually — everyone else is self-serve.',
    },
    {
      q: 'How long am I committed for?',
      a: 'Cancellable monthly. You see your booked plan in the owner panel and end it with one click at the end of the current month. There are no 12-month contracts, no notice periods hidden in the fine print.',
    },
    {
      q: 'Where is my guest data stored?',
      a: 'All data — reservations, guest names, phone numbers, notes — is stored encrypted on servers in Frankfurt am Main. You can find the data processing agreement under Art. 28 GDPR at /avv. No data is transferred to the USA or any third country.',
    },
    {
      q: 'How does the deposit against no-shows work?',
      a: 'You set a threshold — for example: from six people or from Friday 7 PM onwards, a deposit of €10 per person is collected. The money is held on the guest’s card (pre-auth) and only charged if the guest does not show up. If they do show up, the amount is released or deducted from the bill.',
    },
    {
      q: 'Does Rezaro also work with my existing website?',
      a: 'Yes. You receive a small code snippet (widget) that we embed in your website — whether WordPress, Wix, Jimdo or custom-built. The reservation opens directly on your page, the guest never leaves your domain. It can also be integrated into your Google profile and Instagram link.',
    },
    {
      q: 'What happens after the seven trial days?',
      a: 'You provided a plan and your card at sign-up. On the eighth day, the flat fee is charged automatically. If you cancel beforehand — an email to info@rezaro.de or one click in the panel is enough — no charge is made. We remind you 48 hours in advance by email.',
    },
  ],
  tr: [
    {
      q: 'Rezaro aylık ne kadar?',
      a: 'Üç şeffaf fiyat kademesi: Starter €29, Pro €69, Premium €119 aylık. Rezervasyon başına komisyon yok, gizli maliyet yok, aylık iptal edilebilir. 5’ten fazla şube veya özel istekler mi var? O zaman bireysel görüşürüz — diğer herkes self-servis.',
    },
    {
      q: 'Ne kadar süreyle bağlıyım?',
      a: 'Aylık iptal edilebilir. Satın aldığınız paketi işletme panelinde görür ve içinde bulunduğunuz ayın sonunda tek tıkla sonlandırırsınız. 12 aylık sözleşme yok, küçük puntolarla gizlenmiş fesih süreleri yok.',
    },
    {
      q: 'Misafir verilerim nerede saklanıyor?',
      a: 'Tüm veriler — rezervasyonlar, misafir adları, telefon numaraları, notlar — Frankfurt am Main’deki sunucularda şifreli olarak saklanır. GDPR Madde 28 uyarınca veri işleme sözleşmesini /avv adresinde bulabilirsiniz. ABD’ye veya herhangi bir üçüncü ülkeye veri aktarımı yapılmaz.',
    },
    {
      q: 'No-show’a karşı ön ödeme nasıl çalışır?',
      a: 'Bir eşik belirlersiniz — örneğin: altı kişiden itibaren veya Cuma akşamı 19:00’dan itibaren kişi başı 10 € ön ödeme tahsil edilir. Para, misafirin kartında bloke edilir (ön provizyon) ve yalnızca misafir gelmezse tahsil edilir. Gelirse tutar serbest bırakılır veya hesaptan düşülür.',
    },
    {
      q: 'Rezaro mevcut web sitemle de çalışır mı?',
      a: 'Evet. Web sitenize yerleştirdiğimiz küçük bir kod parçacığı (widget) alırsınız — ister WordPress, ister Wix, Jimdo ister kendiniz yaptınız. Rezervasyon doğrudan sayfanızda açılır, misafir alan adınızdan ayrılmaz. Google profili ve Instagram bağlantısına da entegre edilebilir.',
    },
    {
      q: 'Yedi deneme günü sonunda ne olur?',
      a: 'Kayıt sırasında bir plan ve kartınızı eklediniz. Sekizinci gün sabit ücret otomatik olarak tahsil edilir. Önceden iptal ederseniz — info@rezaro.de adresine bir e-posta veya panelde tek tık yeterli — herhangi bir tahsilat yapılmaz. 48 saat öncesinden e-posta ile hatırlatırız.',
    },
  ],
}

export function FaqAccordion({ lang = 'de' }: { lang?: FaqLang } = {}) {
  const [open, setOpen] = useState<number | null>(0)
  const FAQ = FAQ_T[lang]

  return (
    <div className="divide-y divide-pub-rule border-y border-pub-rule">
      <style>{`
        @keyframes faqFade { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
        .animate-faq-fade { animation: faqFade 240ms cubic-bezier(.22,1,.36,1) both; }
      `}</style>
      {FAQ.map((item, i) => {
        const isOpen = open === i
        return (
          <div key={item.q}>
            <button type="button"
              onClick={() => setOpen(isOpen ? null : i)}
              aria-expanded={isOpen}
              className="w-full flex items-center justify-between gap-6 py-6 text-left group">
              <span className={`text-[17px] md:text-[18.5px] font-semibold leading-snug transition-colors ${
                isOpen ? 'text-brand-700' : 'text-pub-ink group-hover:text-brand-700'
              }`}>
                {item.q}
              </span>
              <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border transition-all ${
                isOpen
                  ? 'bg-brand-600 border-brand-600 text-white'
                  : 'border-pub-rule text-pub-muted group-hover:border-brand-600 group-hover:text-brand-700'
              }`}>
                {isOpen ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
              </span>
            </button>
            {isOpen && (
              <div className="pb-7 pr-12 animate-faq-fade">
                <p className="text-[15px] text-pub-muted leading-relaxed max-w-3xl">
                  {item.a}
                </p>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
